import React, { useMemo, useState, useEffect } from 'react';
import { Elements, PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { toast } from 'react-hot-toast';


const StripePaymentElement = ({ amount, currency, onComplete, buttonLabel, mode = 'payment' }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);

  // Check for redirect return from 3D Secure or other payment actions
  useEffect(() => {
    const checkRedirectReturn = async () => {
      if (!stripe) return;

      const urlParams = new URLSearchParams(window.location.search);
      const paymentIntentClientSecret = urlParams.get('payment_intent_client_secret');
      const paymentIntentId = urlParams.get('payment_intent');
      const setupIntentClientSecret = urlParams.get('setup_intent_client_secret');
      const setupIntentId = urlParams.get('setup_intent');

      if (paymentIntentClientSecret || paymentIntentId) {
        try {
          // Retrieve the payment intent to check its status
          const clientSecret = paymentIntentClientSecret || paymentIntentId;
          const { paymentIntent, error } = await stripe.retrievePaymentIntent(clientSecret);

          if (error) {
            setMessage(error.message || 'Payment verification failed');
            onComplete?.({ success: false, error: error.message, paymentIntent: null });
          } else if (paymentIntent) {
            const status = paymentIntent.status;
            if (status === 'succeeded' || status === 'processing') {
              setMessage(`Payment ${status}`);
              onComplete?.({ success: true, paymentIntent });
            } else {
              setMessage(`Payment status: ${status}`);
              onComplete?.({ success: false, error: `Payment ${status}`, paymentIntent });
            }
          }

          // Clean up URL parameters
          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        } catch (error) {
          console.error('Error checking redirect return:', error);
        }
      }

      if (mode === 'setup' && (setupIntentClientSecret || setupIntentId)) {
        try {
          const clientSecret = setupIntentClientSecret || setupIntentId;
          const { setupIntent, error } = await stripe.retrieveSetupIntent(clientSecret);

          if (error) {
            setMessage(error.message || 'Card setup verification failed');
            onComplete?.({ success: false, error: error.message, setupIntent: null });
          } else if (setupIntent) {
            const status = setupIntent.status;
            if (status === 'succeeded' || status === 'processing') {
              setMessage(`Card setup ${status}`);
              onComplete?.({ success: true, setupIntent });
            } else {
              setMessage(`Setup status: ${status}`);
              onComplete?.({ success: false, error: `Setup ${status}`, setupIntent });
            }
          }

          const newUrl = window.location.pathname;
          window.history.replaceState({}, document.title, newUrl);
        } catch (error) {
          console.error('Error checking setup redirect return:', error);
        }
      }
    };

    checkRedirectReturn();
  }, [stripe, onComplete, mode]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!stripe || !elements) {
      return;
    }

    try {
      // Call elements.submit() before any React state updates so the user-gesture
      // context is intact when Apple Pay (or other wallets) open their native sheet.
      // setIsSubmitting beforehand causes a React re-render that can break the gesture
      // context on iOS Safari, silently preventing the Apple Pay sheet from appearing.
      const { error: submitError } = await elements.submit();

      setIsSubmitting(true);
      setMessage(null);

      if (submitError) {
        console.error('[StripePaymentForm] elements.submit error:', submitError);
        const msg = submitError.message || 'Please check your payment information';
        setMessage(msg);
        toast.error(msg);
        setIsSubmitting(false);
        onComplete?.({ success: false, error: submitError.message, paymentIntent: null });
        return;
      }

      if (mode === 'setup') {
        const setupResult = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: window.location.href
          },
          redirect: 'if_required'
        });

        if (!setupResult) {
          return;
        }

        if (setupResult.error) {
          const errorMessage = setupResult.error.message || 'Failed to save payment method';
          setMessage(errorMessage);
          setIsSubmitting(false);
          onComplete?.({ success: false, error: errorMessage, setupIntent: setupResult.setupIntent || null });
          return;
        }

        if (setupResult.setupIntent) {
          const status = setupResult.setupIntent.status;
          if (status === 'succeeded' || status === 'processing') {
            setMessage('Payment method saved');
            setIsSubmitting(false);
            onComplete?.({ success: true, setupIntent: setupResult.setupIntent });
            return;
          }

          setMessage(`Setup status: ${status}`);
          setIsSubmitting(false);
          onComplete?.({ success: false, error: `Setup ${status}`, setupIntent: setupResult.setupIntent });
          return;
        }
      }

      // Now confirm the payment with the collected payment method
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: window.location.href
        },
        redirect: 'if_required'
      });

      // Check if we were redirected (result will be undefined if redirected)
      if (!result) {
        // Payment is being processed via redirect, will return to return_url
        return;
      }

      if (result.error) {
        console.error('[StripePaymentForm] confirmPayment error:', result.error);
        const errorMessage = result.error.message || 'Payment failed';
        setMessage(errorMessage);
        toast.error(errorMessage);
        setIsSubmitting(false);
        onComplete?.({ success: false, error: errorMessage, paymentIntent: result.paymentIntent || null });
        return;
      }

      if (result.paymentIntent) {
        const status = result.paymentIntent.status;
        
        if (status === 'succeeded' || status === 'processing') {
          setMessage(`Payment ${status}`);
          setIsSubmitting(false);
          onComplete?.({
            success: true,
            paymentIntent: result.paymentIntent
          });
        } else {
          setMessage(`Payment status: ${status}`);
          setIsSubmitting(false);
          onComplete?.({ success: false, error: `Payment ${status}`, paymentIntent: result.paymentIntent });
        }
      } else {
        setMessage('Payment confirmation completed but no payment intent returned');
        setIsSubmitting(false);
        onComplete?.({ success: false, error: 'No payment intent returned', paymentIntent: null });
      }
    } catch (error) {
      console.error('[StripePaymentForm] Unexpected error:', error);
      const errorMessage = error.message || 'Payment failed unexpectedly';
      setMessage(errorMessage);
      toast.error(errorMessage);
      setIsSubmitting(false);
      onComplete?.({ success: false, error: errorMessage, paymentIntent: null });
    }

    setIsSubmitting(false);
  };

  const label = buttonLabel || (
    mode === 'setup'
      ? 'Save Card'
      : `Pay ${(amount / 100).toFixed(2)} ${currency.toUpperCase()}`
  );

  // Detect if we're in an iOS app context
  const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) || 
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

  // Add error boundary for PaymentElement
  if (!stripe || !elements) {
    return (
      <div className="p-4 bg-yellow-50 border border-yellow-200 rounded-md">
        <p className="text-sm text-yellow-800">Loading payment form...</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="min-h-[200px]">
        <PaymentElement options={{ layout: 'tabs' }} />
      </div>
      <button
        type="submit"
        disabled={isSubmitting || !stripe || !elements}
        className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSubmitting ? 'Processing…' : label}
      </button>
      {message && (
        <div className={`p-3 rounded-md text-sm ${
          message.includes('succeeded') || message.includes('processing') 
            ? 'bg-green-50 text-green-800' 
            : 'bg-red-50 text-red-800'
        }`}>
          {message}
        </div>
      )}
      {!stripe && (
        <div className="p-3 rounded-md text-sm bg-yellow-50 text-yellow-800">
          Loading Stripe...
        </div>
      )}
      {!elements && stripe && (
        <div className="p-3 rounded-md text-sm bg-yellow-50 text-yellow-800">
          Loading payment form...
        </div>
      )}
    </form>
  );
};

const StripePaymentForm = ({ config, onComplete, buttonLabel, mode = 'payment' }) => {
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const stripePromise = useMemo(() => {
    if (!config?.publishableKey) {
      const errMsg = 'Stripe publishable key is missing';
      console.error('[StripePaymentForm]', errMsg);
      setError(errMsg);
      setIsLoading(false);
      return null;
    }
    try {
      console.log('[StripePaymentForm] Loading Stripe with key:', config.publishableKey.substring(0, 20) + '...');
      const stripe = loadStripe(config.publishableKey);
      return stripe;
    } catch (err) {
      console.error('[StripePaymentForm] Error loading Stripe:', err);
      const errMsg = 'Failed to load Stripe. Please refresh the page.';
      setError(errMsg);
      setLoadError(err.message || 'Unknown error');
      setIsLoading(false);
      return null;
    }
  }, [config?.publishableKey]);

  useEffect(() => {
    const initializeStripe = async () => {
      if (!stripePromise || !config?.clientSecret) {
        return;
      }

      try {
        console.log('[StripePaymentForm] Initializing Stripe Elements...');
        
        // Wait for Stripe to be ready
        const stripeInstance = await stripePromise;
        if (stripeInstance) {
          console.log('[StripePaymentForm] Stripe loaded successfully');
          setIsLoading(false);
        } else {
          throw new Error('Stripe instance is null');
        }
      } catch (err) {
        console.error('[StripePaymentForm] Initialization error:', err);
        setError('Failed to initialize payment form. Please try again.');
        setLoadError(err.message || 'Unknown error');
        setIsLoading(false);
      }
    };

    // Set a timeout to prevent infinite loading
    const timeout = setTimeout(() => {
      if (isLoading) {
        console.warn('[StripePaymentForm] Loading timeout - showing error');
        setError('Payment form is taking too long to load. Please refresh the page.');
        setLoadError('Loading timeout');
        setIsLoading(false);
      }
    }, 10000); // 10 second timeout

    initializeStripe();

    return () => clearTimeout(timeout);
  }, [stripePromise, config?.clientSecret, isLoading]);

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm font-medium text-red-800">{error}</p>
        {loadError && (
          <p className="mt-1 text-xs text-red-600">Error: {loadError}</p>
        )}
        <button
          onClick={() => window.location.reload()}
          className="mt-3 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
        >
          Refresh Page
        </button>
      </div>
    );
  }

  if (!config?.clientSecret || !stripePromise) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4">
        <div className="animate-pulse space-y-4">
          <div className="h-20 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  try {
    return (
      <Elements
        stripe={stripePromise}
        options={{
          clientSecret: config.clientSecret,
          appearance: {
            theme: 'stripe'
          }
        }}
      >
        <StripePaymentElement
          amount={config.amount}
          currency={config.currency}
          onComplete={onComplete}
          buttonLabel={buttonLabel}
          mode={mode}
        />
      </Elements>
    );
  } catch (err) {
    console.error('[StripePaymentForm] Render error:', err);
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-md">
        <p className="text-sm font-medium text-red-800">Failed to render payment form</p>
        <p className="mt-1 text-xs text-red-600">Error: {err.message || 'Unknown error'}</p>
        <button
          onClick={() => window.location.reload()}
          className="mt-3 px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700"
        >
          Refresh Page
        </button>
      </div>
    );
  }
};

export default StripePaymentForm;
